import { nectarApi } from '@/features/nectar/api/client'

/**
 * Mints the Tremendous redemption URL for one payout.
 *
 * IV-31 — the returned string is PERMANENT, UNREVOCABLE BEARER AUTHORITY: anyone who reads it
 * can spend the reward, and minting a replacement does not invalidate earlier copies. The caller
 * must hold it in one local variable for the length of one function and hand it straight to
 * `tab.location.replace`. Never put it in the DOM, React state, a log, a toast, the clipboard,
 * analytics or Sentry.
 *
 * Deliberately a plain async function, NOT a `useMutation`: a mutation's `data` is React state
 * that outlives the click and is readable in React DevTools.
 *
 * Deliberately NOT re-exported from `../api/index.ts` — import it by path.
 *
 * `skipAuthRedirect` (IV6): a vendor-side rejection of our Tremendous API key surfaces as a 401,
 * which the nectar client would otherwise treat as session expiry and log the user out.
 */
export async function mintRedemptionLink(payoutId: string): Promise<string> {
  const { redemptionUrl } = await nectarApi.post<{ redemptionUrl: string }>(
    `/v1/user/payouts/${payoutId}/redemption-link`,
    undefined,
    { skipAuthRedirect: true }
  )
  // The caller navigates a same-origin about:blank tab to this value, so anything but an https URL
  // must not reach it. The error deliberately does not echo the value.
  if (typeof redemptionUrl !== 'string' || !redemptionUrl.startsWith('https://')) {
    throw new Error('Redemption link response did not contain an https URL')
  }
  return redemptionUrl
}
